import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import studioBanner from '../../assets/images/squarebit-studios-banner.jpg';

const CAPABILITIES = ['film', 'game', 'controls', 'pipeline'] as const;

const RiggingServicesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="w-full px-6 md:px-10 py-16 md:py-24">
      {/* Hero */}
      <section className="max-w-[860px]">
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-blue mb-4">
          {t('riggingServices.eyebrow')}
        </div>
        <h1 className="font-display font-extrabold text-[34px] md:text-[52px] tracking-[-0.03em] leading-[1.05] text-content-dark">
          {t('riggingServices.title')}
        </h1>
        <p className="text-content-muted-alt text-[17px] md:text-[19px] leading-[1.6] mt-6 max-w-[680px]">
          {t('riggingServices.subtitle')}
        </p>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] mt-5 max-w-[680px]">
          {t('riggingServices.intro')}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center mt-8 rounded-full bg-blue text-surface-base font-body font-semibold text-[15px] px-[26px] py-[13px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {t('riggingServices.heroCta')}
        </Link>
      </section>

      {/* Studio banner. Sits after the pitch rather than above the headline:
          the characters read as proof of craft once you know what we do, and a
          5.6:1 image above the h1 would push it under the fold on a phone. */}
      <section className="mt-16 md:mt-24">
        <img
          src={studioBanner}
          alt={t('riggingServices.bannerAlt')}
          width={2400}
          height={432}
          loading="lazy"
          decoding="async"
          className="w-full h-auto rounded-[16px] md:rounded-[20px] border border-border-brand"
        />
      </section>

      {/* What we deliver */}
      <section className="mt-20 md:mt-28">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('riggingServices.whatTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] max-w-[620px] mb-10">
          {t('riggingServices.whatSubtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[20px]">
          {CAPABILITIES.map((c) => (
            <div
              key={c}
              className="bg-surface-base border border-border-brand rounded-[16px] p-[24px] md:p-[28px] shadow-[0_2px_12px_rgba(34,211,238,0.06)]"
            >
              <h3 className="font-display font-bold text-[18px] md:text-[20px] text-content-dark mb-[10px]">
                {t(`riggingServices.cap_${c}_title`)}
              </h3>
              <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
                {t(`riggingServices.cap_${c}_body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How we work / bidding */}
      <section className="mt-20 md:mt-28 rounded-[20px] bg-[var(--subscription-brand-100)] border border-border-brand px-[24px] md:px-[48px] py-[36px] md:py-[48px]">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {t('riggingServices.howTitle')}
        </h2>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] max-w-[720px] mb-8">
          {t('riggingServices.howIntro')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-[28px]">
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-blue mb-[8px]">
              {t('riggingServices.bidScopeTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('riggingServices.bidScopeBody')}
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-blue mb-[8px]">
              {t('riggingServices.bidPricingTitle')}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {t('riggingServices.bidPricingBody')}
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mt-20 md:mt-28 mb-4 text-center">
        <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark mb-4">
          {t('riggingServices.ctaTitle')}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[17px] leading-[1.6] max-w-[560px] mx-auto mb-8">
          {t('riggingServices.ctaBody')}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[16px] px-[30px] py-[14px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {t('riggingServices.ctaButton')}
        </Link>
      </section>
    </div>
  );
};

export default RiggingServicesPage;
